import { QUOTE_ACCEPTANCE_CHANNELS, type AcceptQuoteInput, type QuoteAcceptanceChannel } from '@chukua/common';
import { Button, Form, Input, Modal, Select } from 'antd';
import { useState } from 'react';
import { QUOTE_ACCEPTANCE_CHANNEL_LABELS } from '../policies/quote-acceptance-channel-labels';

interface AcceptFormValues {
  acceptedByName: string;
  acceptanceChannel: QuoteAcceptanceChannel;
}

interface RejectFormValues {
  reason: string;
}

interface QuoteDecisionModalProps {
  decision: 'accept' | 'reject' | null;
  jobNumber: string;
  onCancel: () => void;
  onAccept: (input: AcceptQuoteInput) => Promise<void>;
  onReject: (reason: string) => Promise<void>;
}

/**
 * Accept (acceptance channel) and reject (reason) on a quote (gitaumoses4/chukua#34) — split out
 * of `JobQuotePanel`, same idiom as `CreditHoldDecisionModal`: one modal serves both buttons.
 */
export function QuoteDecisionModal({ decision, jobNumber, onCancel, onAccept, onReject }: QuoteDecisionModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [acceptForm] = Form.useForm<AcceptFormValues>();
  const [rejectForm] = Form.useForm<RejectFormValues>();

  async function submitAccept(values: AcceptFormValues) {
    setSubmitting(true);
    try {
      await onAccept(values);
      acceptForm.resetFields();
    } finally {
      setSubmitting(false);
    }
  }

  async function submitReject(values: RejectFormValues) {
    setSubmitting(true);
    try {
      await onReject(values.reason);
      rejectForm.resetFields();
    } finally {
      setSubmitting(false);
    }
  }

  const accepting = decision === 'accept';

  return (
    <Modal
      open={decision !== null}
      title={accepting ? `Accept job ${jobNumber}'s quote` : `Reject job ${jobNumber}'s quote`}
      onCancel={onCancel}
      footer={null}
      destroyOnHidden
    >
      {accepting ? (
        <Form<AcceptFormValues> form={acceptForm} layout="vertical" requiredMark={false} onFinish={submitAccept} disabled={submitting}>
          <Form.Item name="acceptedByName" label="Who accepted?" rules={[{ required: true, message: "Enter the customer contact's name" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="acceptanceChannel" label="How did the customer accept?" rules={[{ required: true, message: 'Choose how the customer accepted' }]}>
            <Select options={QUOTE_ACCEPTANCE_CHANNELS.map((channel) => ({ value: channel, label: QUOTE_ACCEPTANCE_CHANNEL_LABELS[channel] }))} />
          </Form.Item>
          <Form.Item className="mb-0 flex justify-end">
            <Button type="primary" htmlType="submit" loading={submitting}>
              Accept
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <Form<RejectFormValues> form={rejectForm} layout="vertical" requiredMark={false} onFinish={submitReject} disabled={submitting}>
          <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Enter a reason' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item className="mb-0 flex justify-end">
            <Button type="primary" htmlType="submit" loading={submitting}>
              Reject
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
